// tslint:disable max-func-body-length
import * as React from 'react';
import { OptionsProps } from 'app/components/options-handler';
import { ExtraInfo } from 'app/components/access-panel';
import { UserTypeGroup } from './user-type-group';
import { UnitLocationLang } from './unit-location-lang';
import { Component, RefObject } from 'react';
import { withMetroLine } from 'app/components/metro-line/with-metro-line';
import { WithMetroLineProps, WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { UserEditSectionType, AdministrationUserEditProps } from 'app/area/administration/edit/user/types';
import { userOptions } from '../endpoints';
import { AlternativeId } from './alternative-id/alternative-id';
import { AdminPaperSubPanel, AdminPaperTopPanel } from 'app/area/administration/edit/components';
import { Details } from 'app/area/administration/edit/user/details';
import { Person } from 'app/components/access-panel/shared/types';
import { simpleFieldForPath } from 'app/infrastructure/simple-form';
import { AdministrationEditTranslation } from 'app/area/administration/edit/constants';
import { FormValuesType, ApiToUse, FormOptions } from 'app/infrastructure/simple-form/types';
import { STATE } from 'app/constants';
import { MetroLineSection } from 'app/components/metro-line/metro-line-section';
import { RoleAccess } from 'app/area/administration/edit/user/roles/panel-role-access';
import { sfNames } from 'app/components/access-panel/sf-names';
import { ComplexValue } from 'app/types-shared';
import {
  PanelsProps, Operation, DefaultValues, AccessToScreen, UserOccurence, ExtendedAccess,
  RestrictedAccess, Notifications, AccessToAssignRoles, AccessToAdministrateUserGroup
} from 'app/components/access-panel';
import { PersonActionType } from 'app/area/administration/shared/types';
import { ExperienceTransferAlerts } from 'app/components/access-panel/experience-transfer-alerts';
import {
  displayAssignRoleSection, displayRolePanel, displayAssignRolePanel
} from 'app/area/administration/edit/user/helpers';

interface UserEditFormExternalProps extends WithMetroLineProps<UserEditSectionType> {
  formValues: FormValuesType;
  role: {
    id: number;
    fetching: boolean;
    data?: Person;
  };
  selectedUserType: number;
  extraInfoPanel: boolean;
  alternativeIdPanel: boolean;
  showPersonId: boolean;
  translation: AdministrationEditTranslation;
  sectionRef?: RefObject<HTMLDivElement>;
  defaultRole?: ComplexValue<number>;
  formOptions?: FormOptions;
  clearValidation: (formName: string, parameters?: { [key: string]: number | string }) => void;
}

const SimpleForm = simpleFieldForPath(STATE.administrationEdit);

export type UserEditFormComponentProps = AdministrationUserEditProps & UserEditFormExternalProps & OptionsProps
  & WithMetroLineProps<UserEditSectionType> & WithMetroLineWrappedComponentProps<UserEditSectionType>;

export class UserEditFormComponent extends Component<UserEditFormComponentProps> {

  private isValueFieldsReadOnly = (optionsColection: ComplexValue<number>, caseType: ComplexValue<number>, userType: number) => {
    return !optionsColection || !caseType || !userType;
  };

  public render() {
    const { alternativeIdPanel, extraInfoPanel, formValues, role, rolePanelAccess, translation, accessData, personOptionsCodes,
      data, showPersonId, editMode, clearValidation, validation, selectedUserType, loadRole, clearValueInStore, updateAccessData,
      options, loadOptions, params: { actionType } = {}, defaultRole, simpleForm, allowedAccessBasedRoles, hasRoles,
      formOptions,
      sharedState: {
        access: {
          adminPanel: {
            user: {
              administrationCentral,
              read, create
            },
            restrictedAccessPanel,
            experienceTransferAlertsPanel
          }
        }
      } } = this.props;

    const fetchingRoleBasedData = role ? role.fetching : false;

    const roleAccessDefaultRole = allowedAccessBasedRoles.find(accessBasedRole => accessBasedRole.v === defaultRole?.value)
      ? defaultRole : undefined;

    const panelProps: PanelsProps = {
      translation,
      formComponent: SimpleForm,
      formValues,
      validation,
      clearValueInStore,
      endpoints: userOptions,
      readOnly: !editMode,
      data,
      roleSelected: !!role && (!!role.data || role.fetching),
      roleBasedData: role && role.data,
      fetchingRoleBasedData: role ? role.fetching : false,
      accessData,
      personOptionsCodes,
      levelsNotAllowed: { accessToScreen: true },
      updateAccessData,
      clearValidation,
      options,
      loadOptions,
      simpleForm,
      formOptions
    };

    return <div>
      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.Details}>

        <Details
          actionType={actionType}
          formComponent={SimpleForm}
          data={data}
          showPersonId={showPersonId}
          readOnly={!editMode}
          translation={translation}
          intl={this.props.intl} />

        {extraInfoPanel && <ExtraInfo
          readOnly={!editMode}
          formComponent={SimpleForm}
          roleSelected={!!role && (!!role.data || role.fetching)}
          roleBasedData={role && role.data}
          fetchingRoleBasedData={fetchingRoleBasedData}
          endpoints={userOptions}
          {...this.props}
        />}

        {!!alternativeIdPanel && <AlternativeId formComponent={SimpleForm}
          data={data && data.alternativeIds}
          formValues={formValues}
          validation={validation && validation.alternativeIds}
          clearValidation={clearValidation}
          readOnly={!editMode}
          translation={translation} />}

        <UserTypeGroup formComponent={SimpleForm} {...this.props} />

        <AdminPaperSubPanel title={translation.unit_location_usergroup}>
          <UnitLocationLang formComponent={SimpleForm} {...this.props} />
        </AdminPaperSubPanel>

        {displayRolePanel(rolePanelAccess, editMode, data?.accessRole, administrationCentral, read, create, hasRoles)
          && <RoleAccess
            initialValue={roleAccessDefaultRole || data && data.accessRole}
            formValue={formValues[sfNames.accessRole] as ComplexValue<number>}
            roleBasedId={role && role.id}
            fetchingRoleBasedData={fetchingRoleBasedData}
            readOnly={!editMode}
            translation={translation}
            loadRole={loadRole}
            formComponent={SimpleForm}
            clearValueInStore={clearValueInStore} />}
      </MetroLineSection>

      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.AccessToOperation}>
        <Operation {...panelProps} />
      </MetroLineSection>

      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.AccessToScreen}>
        <AccessToScreen {...panelProps} />
      </MetroLineSection>

      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.ExtendedAccess}>
        <ExtendedAccess {...panelProps} />
      </MetroLineSection>

      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.RestrictedAccess}>
        {restrictedAccessPanel && <RestrictedAccess {...panelProps} />}
      </MetroLineSection>

      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.AccessToAdministrateUserGroup}>
        <AccessToAdministrateUserGroup {...panelProps} roleSelected={false} />
      </MetroLineSection>

      {displayAssignRoleSection(hasRoles, data?.accessToAssignRoles) &&
        <MetroLineSection<UserEditSectionType>
          {...this.props}
          sectionType={UserEditSectionType.AccessToAssignRoles}>
          {<AdminPaperTopPanel title={translation.access_to_assign_roles_title}>
            {displayAssignRolePanel(rolePanelAccess, administrationCentral, editMode, data)
              ? <AccessToAssignRoles {...panelProps} roleSelected={false} />
              : <p className="no-content">{translation.no_fields_available}</p>}
          </AdminPaperTopPanel>}
        </MetroLineSection>}

      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.DefaultValues}>
        {<AdminPaperTopPanel title={translation.default_values}>
          <DefaultValues
            apiToUse={ApiToUse.ADMINAPI}
            userType={selectedUserType}
            isValueFieldsReadOnly={this.isValueFieldsReadOnly}
            isUserTypeRequired={true}
            {...panelProps} />
        </AdminPaperTopPanel>}
      </MetroLineSection>

      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.UserOccurence}>
        <UserOccurence
          {...panelProps}
          readOnly={!editMode || (actionType === PersonActionType.edit && data?.technical)} />
      </MetroLineSection>

      <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.Notifications}>
        <Notifications {...panelProps} roleSelected={false} />
      </MetroLineSection>

      {experienceTransferAlertsPanel && <MetroLineSection<UserEditSectionType>
        {...this.props}
        sectionType={UserEditSectionType.ExperienceTransferAlerts}>
        <ExperienceTransferAlerts {...panelProps} />
      </MetroLineSection>}
    </div>;
  }
}

export const UserEditForm = withMetroLine<UserEditFormExternalProps, UserEditSectionType>()(UserEditFormComponent);
